import React, { useEffect, useState } from "react";
import MemberAreaFooter from "../components/MemberAreaFooter";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Building2, Home, Image as ImageIcon, Menu, Play, ShoppingBag, UserCircle, Users } from "lucide-react";
import { api } from "../lib/api";
import { videoCast, dedupeVideoCast, castSlug } from "../lib/videoCast";
import { CAST_TAB, ESCORT_TAB, FREE_VIDEOS_TAB, GALLERY_TAB, STUDIO_SCENES_TAB, STORE_TAB, buildTabSearch } from "../lib/freeArea";
import FeaturedVideoCast from "../components/FeaturedVideoCast";
import FreeAreaSidebar from "../components/FreeAreaSidebar";
import VideoThumbnail from "../components/VideoThumbnail";

export default function CastProfile() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [status, setStatus] = useState("loading");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  useEffect(() => {
    let mounted = true;
    Promise.all([
      api.get("/api/member-content/tier/free/videos"),
      api.get("/api/videos/studio-scenes").catch(() => ({data: []})),
    ]).then(([free, studio]) => {
      const byId = new Map([...(free.data.videos || []), ...(Array.isArray(studio.data) ? studio.data : [])].map((video) => [video.id, video]));
      if (mounted) { setVideos([...byId.values()]); setStatus("ready"); }
    }).catch(() => { if (mounted) setStatus("error"); });
    return () => { mounted = false; };
  }, [slug]);
  const matches = (person) => decodeURIComponent(castSlug(person)) === slug;
  const person = dedupeVideoCast(videos.flatMap(videoCast)).find(matches);
  const related = videos.filter((video) => videoCast(video).some(matches));
  const photoCandidates = person ? related.flatMap((video) => {
    const sources = [
      ...(Array.isArray(video.gallery_photos) ? video.gallery_photos : []),
      ...(Array.isArray(video.scene_photos) ? video.scene_photos : []),
      video.cover_image,
      video.thumbnail_url,
    ];
    return sources.map((photo, index) => {
      const src = typeof photo === "string" ? photo : photo?.image_url || photo?.url || photo?.thumbnail_url;
      return src ? { src, alt: `Photo ${index + 1} from ${video.title || "a related video"}` } : null;
    }).filter(Boolean);
  }) : [];
  const seenPhotos = new Set();
  const profilePhotos = photoCandidates.filter(({ src }) => {
    if (src === person?.photo || seenPhotos.has(src)) return false;
    seenPhotos.add(src);
    return true;
  });
  const changeTab = (tab) => navigate(`/members/free${buildTabSearch("", tab)}`);
  const handleLogout = async () => {
    try {
      await api.post("/api/auth/logout");
      navigate("/members/login");
    } catch { /* Leave the profile open if logout cannot complete. */ }
  };
  const memberLinks = [
    { id: FREE_VIDEOS_TAB, label: "Videos", mobileLabel: "Videos", icon: Play },
    { id: STUDIO_SCENES_TAB, label: "Studio Scenes", mobileLabel: "Studios", icon: Building2 },
    { id: GALLERY_TAB, label: "Gallery", mobileLabel: "Gallery", icon: ImageIcon },
    { id: STORE_TAB, label: "Shop & Affiliate", mobileLabel: "Shop", icon: ShoppingBag },
    { id: ESCORT_TAB, label: "Companion", mobileLabel: "Companion", icon: UserCircle },
    { id: CAST_TAB, label: "Cast & Crew", mobileLabel: "Cast", icon: Users },
  ];
  return <div className="min-h-screen bg-[#08090D] text-white">
    <FreeAreaSidebar activeTab={CAST_TAB} sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} changeTab={changeTab} handleLogout={handleLogout} />
    <div className="min-h-screen pb-24 lg:ml-64">
      <header className="sticky top-0 z-30 border-b border-white/10 bg-[#08090D]/90 px-4 py-3 backdrop-blur-lg lg:hidden">
        <button type="button" aria-label="Open member navigation" aria-expanded={sidebarOpen} onClick={() => setSidebarOpen(true)} className="inline-flex min-h-11 items-center gap-2 rounded-md px-2 text-sm text-zinc-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white">
          <Menu size={20} /> Browse
        </button>
      </header>
      <main className="min-h-screen px-4 py-6 text-white sm:px-6 sm:py-10">
    <div className="mx-auto max-w-7xl space-y-8">
      <Link to="/members/free?tab=cast" className="-ml-2 inline-flex min-h-11 items-center rounded-md px-2 text-sm text-pink-300 underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white">Back to Cast &amp; Crew</Link>
      <h1 className="text-3xl font-bold">{person?.stage_name || "Cast profile"}</h1>
      {status === "loading" ? <p role="status">Loading profile…</p> : status === "error" ? <p role="alert">Unable to load this profile. Please sign in and try again.</p> : !person ? <p>No video-linked profile found.</p> : <>
        <FeaturedVideoCast people={[person]} additionalPhotos={profilePhotos} profileLayout />
        <section><h2 className="mb-5 text-xl font-bold">Videos with {person.stage_name}</h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">{related.map((video) => <Link key={video.id} to={`/video/${encodeURIComponent(video.id)}/preview`} className="min-w-0 overflow-hidden rounded-lg border border-white/10 bg-[#0F1118]">
            <VideoThumbnail video={video} freeAreaMode /><h3 className="line-clamp-2 p-3 text-sm font-semibold">{video.title}</h3>
          </Link>)}</div>
        </section>
      </>}
    </div>
      </main>
      <MemberAreaFooter />
    </div>
    <nav aria-label="Member sections" className="fixed inset-x-0 bottom-0 z-40 border-t border-white/20 bg-[#0F1118] pb-[env(safe-area-inset-bottom)] lg:left-64">
      <div className="mx-auto grid max-w-5xl grid-cols-7 gap-1 px-1 py-1 sm:px-3">
        {memberLinks.map(({ id, label, mobileLabel, icon: Icon }) => <Link key={id} to={`/members/free${buildTabSearch("", id)}`} aria-label={label} aria-current={id === CAST_TAB ? "page" : undefined} className={`flex min-h-14 min-w-0 flex-col items-center justify-center gap-1 rounded-md px-0.5 py-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white ${id === CAST_TAB ? "bg-gradient-to-r from-[#E11D48] to-[#FF10F0] text-white" : "text-[#B8B8C2] hover:text-white"}`}>
          <Icon size={20} aria-hidden="true" />
          <span className="text-[9px] font-semibold sm:text-[10px]">{mobileLabel}</span>
        </Link>)}
        <Link to="/" aria-label="Home" className="flex min-h-14 min-w-0 flex-col items-center justify-center gap-1 rounded-md px-0.5 py-2 text-[#B8B8C2] hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white">
          <Home size={20} aria-hidden="true" />
          <span className="text-[9px] font-semibold sm:text-[10px]">Home</span>
        </Link>
      </div>
    </nav>
  </div>;
}
